<script lang="ts" setup>
import AmazonProduct from "@/components/AmazonProduct.vue";
import AppButton from "@/components/AppButton.vue";
import BaseAvatar from "@/components/BaseAvatar.vue";
import BasePageHeading from "@/components/BasePageHeading.vue";
import BasePagination from "@/components/BasePagination.vue";
import BaseSearchBar from "@/components/BaseSearchBar.vue";
import BaseSwitch from "@/components/BaseSwitch.vue";
import DokkanCharacter from "@/components/DokkanCharacter.vue";
import DokkanCharacterList from "@/components/DokkanCharacterList.vue";
import TheCharacterFilterSidebar from "@/components/TheCharacterFilterSidebar.vue";
import TheLayout from "@/components/TheLayout.vue";
import useFiltersDeprecated from "@/composables/filters-deprecated";
import useParams from "@/composables/params";
import useToast from "@/composables/toast";
import { PaginatedCharacterData } from "@/types";
import { usePage } from "@inertiajs/vue3";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import axios from "axios";
import { reactive, ref, watch } from "vue";
import { route } from "ziggy-js";

const breakpoints = useBreakpoints(breakpointsTailwind);

// TODO: strictly type
const props = defineProps<{
  characters: PaginatedCharacterData;
  link_skills: object[];
  categories: object[];
  box?: object;
}>();

const page = usePage();
const params = useParams();

const sidebarOpen = ref(false);
const filters = reactive({
  ...params.value,
});

//==============================================================================
// User Box
//==============================================================================

const boxPublic = ref<boolean>(props.box?.public);

const setBoxVisibility = (publicVisibility: boolean) => {
  axios
    .patch(route("users.box.visibility"), {
      box_public: publicVisibility,
    })
    .then(() => {
      useToast({
        title: "Success",
        text: `Box visibility set to ${publicVisibility ? "public" : "private"}.`,
      });
    })
    .catch(() => {
      useToast({
        title: "Oops!",
        text: "Something went wrong. Please try again.",
        type: "error",
      });
    });
};

watch(
  () => boxPublic.value,
  (value) => setBoxVisibility(value),
);

const copyBoxUrl = () => {
  navigator.clipboard
    .writeText(
      route(route().current(), {
        user: props.box.user.id,
      }),
    )
    .then(() => {
      useToast({
        title: "Success",
        text: "Link to your box copied.",
      });
    });
};
</script>

<template>
  <TheLayout
    :description="
      (box === null
        ? 'A highly filterable database'
        : `View ${box.user.name}'s public box`) +
      ' of DRAGON BALL Z Dokkan Battle characters. Find characters based on skill effects, super attacks, link skills, categories &amp; more!'
    "
    :has-page-heading="!box"
    :heading-href="route('characters.index')"
    :title="
      box !== null
        ? `${box.user.name}'s Dokkan Battle Box`
        : 'Dokkan Battle Characters'
    "
    heading="Characters"
  >
    <template #navbar>
      <div class="flex grow justify-between gap-4 md:grow-0">
        <!-- Search Bar -->
        <BaseSearchBar
          v-model="filters.search"
          class="w-full"
          placeholder='Search e.g. "AGL Goku"&hellip;'
        />

        <AppButton
          :active="sidebarOpen"
          class="hidden sm:inline-flex"
          icon="filter"
          @click="sidebarOpen = !sidebarOpen"
          >Filters
        </AppButton>
        <AppButton
          :active="sidebarOpen"
          class="sm:hidden"
          @click="sidebarOpen = !sidebarOpen"
        >
          <i-fa6-solid-filter />
        </AppButton>
      </div>
    </template>

    <section class="flex flex-col gap-4">
      <div
        v-if="box"
        class="mx-auto flex flex-col items-center gap-4 sm:flex-row"
      >
        <BaseAvatar :user="box.user" />

        <div
          class="flex flex-col items-center gap-2 px-4 sm:items-start sm:px-0"
        >
          <BasePageHeading class="!p-0">
            {{ box.user.name }}'s Box
          </BasePageHeading>

          <AppButton
            v-if="page.props.user?.id !== box?.user.id"
            :href="route('teams.create', { box: box.user.id })"
            >Team Builder
          </AppButton>

          <BaseSwitch
            v-if="page.props.user?.id === box?.user.id"
            v-model="boxPublic"
            left="Private"
            right="Public"
          />

          <div
            v-if="page.props.user?.id === box?.user.id"
            class="group flex max-w-full items-center rounded-lg border-2 border-green-900 bg-black shadow shadow-inner hover:border-green-600"
          >
            <span class="truncate px-2">{{
              route(route().current(), { user: box.user.id })
            }}</span>
            <span
              class="border-l-2 border-green-900 bg-green-900/50 p-2 hover:cursor-pointer group-hover:border-green-600"
              @click="copyBoxUrl"
              ><i-fa6-solid-copy
            /></span>
          </div>
        </div>
      </div>

      <div class="flex justify-between gap-4 px-4 sm:px-0">
        <div class="flex flex-wrap gap-2">
          <AppButton
            :active="filters.rarities?.indexOf('5') > -1"
            class="grow-0 !px-1 !py-0"
            @click="useFiltersDeprecated(filters).updateArray('rarities', 5)"
          >
            <img
              :src="`${page.props.app.cdn_url}/assets/en/layout/en/image/cha_rare_sm_lr.png`"
              alt=""
              class="-my-3"
            />
          </AppButton>

          <AppButton
            :active="filters.leader_skill_multiplier === '200'"
            class="grow-0"
            @click="
              filters.leader_skill_multiplier =
                filters.leader_skill_multiplier === '200' ? '' : '200'
            "
          >
            200% Leaders
          </AppButton>
        </div>

        <div class="flex items-end">
          <AppButton
            :active="route().params.compact !== 'true'"
            :class="{
              'border-r-0': route().params.compact === 'true',
            }"
            class="h-fit rounded-r-none"
            @click="filters.compact = false"
          >
            <template #icon>
              <i-fa6-solid-table-cells-large />
            </template>
          </AppButton>
          <AppButton
            :active="route().params.compact === 'true'"
            :class="{
              'border-l-0': route().params.compact !== 'true',
            }"
            class="h-fit rounded-l-none"
            @click="filters.compact = true"
          >
            <template #icon>
              <i-fa6-solid-table-cells />
            </template>
          </AppButton>
        </div>
      </div>

      <div
        v-if="route().params.compact === 'true'"
        class="flex flex-wrap justify-center gap-2 px-4 sm:px-2"
      >
        <DokkanCharacterList :characters="characters.data" />
      </div>

      <div
        v-else
        class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
      >
        <template v-for="(character, i) in characters.data" :key="character.id">
          <DokkanCharacter :character="character" />

          <AmazonProduct
            v-if="
              (breakpoints.greaterOrEqual('2xl').value && i === 3) ||
              (breakpoints.smaller('2xl').value && i === 5)
            "
            :amount="
              breakpoints.smallerOrEqual('sm').value
                ? 1
                : breakpoints.greaterOrEqual('lg').value
                  ? 5
                  : 3
            "
            class="col-span-full"
          />
        </template>
      </div>

      <BasePagination :filters="filters" :meta="characters.meta" />
    </section>

    <template #sidebar>
      <TheCharacterFilterSidebar
        v-model:open="sidebarOpen"
        :categories="categories"
        :filters="filters"
        :link_skills="link_skills"
      />
    </template>
  </TheLayout>
</template>
